import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  DirectMessageCard,
  MessageVenueCard,
} from '../../../Components/Cards/VenueCards/VenueCards'
import {
  DirectEnquiryMessageCardFragment,
  MatchedVenueCardFragment,
  VenueCardFragment,
} from '../../../Components/Cards/VenueCards/VenueCards.generated'
import VenueCardContainer from '../../../Components/Material/Card/CardContainer'
import actorIsHeadBoxStaff from '../../../Utilities/actorIsHeadBoxStaff'
import { useMarkCardAsSeenMutation } from './CardContainer.generated'
import { captureException } from '@sentry/browser'
import { CardListItem } from '../../../Components/Material/Card/CardStyles'
import EmptyState from '../../../Components/Material/Card/EmptyState'
import MatchesAndDeclines from './MatchesAndDeclines'
import { MessagesBanner } from './MessagesBanner'
import { Actions } from '../EventPage'
import { useDevice, isTouchDevice } from 'use-ua-parser-js'
import { Link, Typography } from '@mui/material'
import { RelevancyFit } from '../../../generated-types'
import { partition } from 'lodash'
import { CardAccordion } from '../../../Components/Material/Card/MultipleFitCardContainer/CardDrawer'
import DeclineCard from '../../../Components/Material/Card/DeclineCard'

const useMarkCardAsSeen = ({
  goodFitResponses,
  averageFitResponses,
  markAverageResponses,
  secret,
}: {
  goodFitResponses: Array<VenueCardFragment | null>
  averageFitResponses: Array<VenueCardFragment | null>
  markAverageResponses: boolean
  secret: string
}): void => {
  const [{ error: markCardAsSeenError }, markCardAsSeen] =
    useMarkCardAsSeenMutation()
  const [searchParams] = useSearchParams()

  const cardsToMark = markAverageResponses
    ? [...goodFitResponses, ...averageFitResponses]
    : goodFitResponses

  useEffect(() => {
    cardsToMark.forEach((response) => {
      const message = response?.message
      if (!message || markCardAsSeenError) return

      if (message.cardSeen || actorIsHeadBoxStaff(searchParams)) return

      markCardAsSeen({
        messageId: message.id,
        accessSecret: secret,
      }).catch(captureException)
    })
  }, [
    goodFitResponses.length,
    averageFitResponses.length,
    markAverageResponses,
    secret,
  ])

  useEffect(() => {
    if (!markCardAsSeenError) return
    captureException(markCardAsSeenError)
  }, [markCardAsSeenError])
}

const MessagesFromVenues = ({
  responses,
  status,
  actions,
  secret,
  showDeclineReminder,
}: {
  actions: Actions
  responses: Array<VenueCardFragment | null>
  secret: string
  status?: string | null
  showDeclineReminder: boolean
}): JSX.Element => {
  const { openHowDoesThisWork } = actions
  const device = useDevice()
  const [cardAccordionOpen, setCardAccordionOpen] = useState(true)
  const isTouchScreen = !!device && isTouchDevice(device)
  const [goodFitResponses, averageFitResponses] = partition(
    responses,
    (res) => res?.message?.relevancyFit === RelevancyFit.Good
  )
  const hasGoodFitResponses = !!goodFitResponses.length
  const hasAverageFitResponses = !!averageFitResponses.length
  const hasBothFitResponses = hasGoodFitResponses && hasAverageFitResponses

  const mapResponsesToCards = (
    responses: Array<VenueCardFragment | null>,
    addDeclineReminderCard: boolean,
    size?: 'small'
  ): JSX.Element[] => {
    const cards = responses.map((interest) => {
      if (!interest) {
        throw new Error('Message was null')
      }
      return (
        <CardListItem key={interest.id} size={size}>
          <MessageVenueCard
            isTouchScreen={isTouchScreen}
            interest={interest}
            actions={actions}
            size={size}
          />
        </CardListItem>
      )
    })

    return addDeclineReminderCard
      ? [...cards, <DeclineCard key='decline-reminder' size={size} />]
      : cards
  }

  useMarkCardAsSeen({
    secret,
    goodFitResponses,
    averageFitResponses,
    markAverageResponses: cardAccordionOpen || !hasGoodFitResponses,
  })

  const goodFitMessageCards = mapResponsesToCards(
    goodFitResponses,
    showDeclineReminder && hasGoodFitResponses
  )

  const averageFitMessageCards = mapResponsesToCards(
    averageFitResponses,
    showDeclineReminder && !hasGoodFitResponses,
    'small'
  )

  const ContentHeader = (): JSX.Element | null => {
    return hasGoodFitResponses ? (
      <Typography
        className='subtitle'
        minWidth='80%'
        order={2}
        sx={{
          marginBottom: '1.5rem',
          fontWeight: 600,
          fontSize: '20px',
          lineHeight: '2em',
        }}
      >
        These are the best venues for you
      </Typography>
    ) : null
  }

  return (
    <>
      <VenueCardContainer
        title='Your messages from venues 💌'
        banner={<MessagesBanner onClick={openHowDoesThisWork} />}
        emptyState={
          <EmptyState contentType='messages'>
            New messages from suitable venues will appear here. We’ll email you
            when they come in.
            <Link
              variant='anchor'
              underline='none'
              onClick={() => openHowDoesThisWork()}
              className='how'
              component='button'
            >
              How does this work?
            </Link>
          </EmptyState>
        }
        contentHeader={<ContentHeader />}
        status={status}
        actions={actions}
        noBottomMargin={hasBothFitResponses}
      >
        {hasGoodFitResponses ? goodFitMessageCards : averageFitMessageCards}
      </VenueCardContainer>
      {hasBothFitResponses && (
        <CardAccordion
          open={cardAccordionOpen}
          setOpen={setCardAccordionOpen}
          cards={averageFitMessageCards}
        />
      )}
    </>
  )
}

const OriginalDirectEnquiry = ({
  enquiries,
}: {
  enquiries: Array<DirectEnquiryMessageCardFragment | null>
}): JSX.Element => {
  const directEnquiryCard = enquiries.map((interest) => {
    if (!interest) {
      throw new Error('Message was null')
    }
    return (
      <CardListItem key={interest.id}>
        <DirectMessageCard key={interest.id} {...interest} />
      </CardListItem>
    )
  })

  return (
    <VenueCardContainer
      title='Your original enquiry 💬'
      subtitle='The venue will be in touch soon if they are able to host you. '
    >
      {directEnquiryCard}
    </VenueCardContainer>
  )
}

export const CardContainer = ({
  responses,
  matches,
  direct,
  declined,
  status,
  actions,
  secret,
  showDeclineReminder,
}: {
  responses: Array<VenueCardFragment | null>
  matches: Array<MatchedVenueCardFragment | null>
  direct: Array<DirectEnquiryMessageCardFragment | null>
  declined: Array<DirectEnquiryMessageCardFragment | null>
  status?: string | null
  actions: Actions
  secret: string
  showDeclineReminder: boolean
}): JSX.Element | null => {
  const shouldShowMatchesAndDeclines =
    matches.length > 0 || responses.length > 0 || declined.length > 0
  return (
    <>
      <MessagesFromVenues
        actions={actions}
        status={status}
        responses={responses}
        secret={secret}
        showDeclineReminder={showDeclineReminder}
      />
      {shouldShowMatchesAndDeclines && (
        <MatchesAndDeclines matches={matches} declines={declined} />
      )}
      {direct.length > 0 && <OriginalDirectEnquiry enquiries={direct} />}
    </>
  )
}
